import axios from "axios";

// SECURITY GUARD — Web Push / Notifications:
// This web app deliberately uses NO Notifications API and NO Web Push. If push is ever added,
// notification bodies MUST be static (e.g. "New message") and MUST NEVER contain decrypted
// message content, sender-supplied text, or any E2EE payload. Push payloads transit third-party
// (APNs/FCM) infrastructure and are a known interception vector — keep them content-free.

const BLOQ_API = process.env.REACT_APP_API_BASE || process.env.REACT_APP_BLOQ_API_URL;

export const API_BASE = `${BLOQ_API}/api`;
export const WS_URL = `${BLOQ_API.replace(/^http/, "ws")}/api/ws`;

export const client = axios.create({ baseURL: API_BASE });

client.interceptors.request.use((config) => {
  const token = localStorage.getItem("bloq_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Single-flight access-token refresh: on a 401, silently POST /auth/refresh once, swap the stored
// token and retry the original request. Only wipe the session if the refresh itself fails.
let refreshPromise = null;
function refreshAccessToken() {
  if (!refreshPromise) {
    refreshPromise = client
      .post("/auth/refresh")
      .then((r) => {
        const tok = r.data?.token;
        if (tok) localStorage.setItem("bloq_token", tok);
        return tok;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

client.interceptors.response.use(
  (res) => res,
  async (error) => {
    const status = error?.response?.status;
    const cfg = error.config || {};
    const url = cfg.url || "";
    const onAuthPages =
      window.location.pathname.startsWith("/login") ||
      window.location.pathname.startsWith("/signup");
    // Some endpoints use 401 as a business/validation error (e.g. a wrong password on account
    // deletion or a wrong current password on change). A failed re-auth on those must NOT wipe
    // the active session. Never try to refresh on the auth endpoints themselves.
    const businessAuth401 =
      url.includes("/auth/delete-account") || url.includes("/auth/change-password");
    const isAuthCall =
      url.includes("/auth/refresh") ||
      url.includes("/auth/login") ||
      url.includes("/auth/signup") ||
      url.includes("/auth/2fa");
    const eligible =
      status === 401 && localStorage.getItem("bloq_token") && !onAuthPages && !businessAuth401 && !isAuthCall;

    if (eligible && !cfg._retried) {
      cfg._retried = true;
      try {
        const tok = await refreshAccessToken();
        if (tok) {
          cfg.headers = cfg.headers || {};
          cfg.headers.Authorization = `Bearer ${tok}`;
          return client(cfg);
        }
      } catch {
        /* refresh failed — fall through to sign-out */
      }
      localStorage.removeItem("bloq_token");
    } else if (eligible) {
      // Already retried once and still 401 → the session is dead.
      localStorage.removeItem("bloq_token");
    }
    // §13.4: every 429 upgrade_required opens ONE shared upsell sheet (UpsellSheet listens) —
    // never a raw error. Callers may still add feature-specific handling.
    if (status === 429) {
      const d = error.response?.data || {};
      const detail = typeof d.detail === "object" && d.detail ? d.detail : d;
      if (detail?.code === "upgrade_required" && !cfg._silentUpsell) {
        window.dispatchEvent(new CustomEvent("bloq:upsell", { detail: { feature: detail.feature, message: detail.message, plan: detail.plan || detail.required_plan } }));
      }
    }
    return Promise.reject(error);
  },
);

export const unwrap = (p) => p.then((r) => r.data);

export const api = {
  // Public app config (Turnstile mode + site key, launch flags).
  getPublicConfig: () => unwrap(client.get("/config/public")),

  // Auth
  login: (email, password, turnstile_token) =>
    unwrap(client.post("/auth/login", { email, password, ...(turnstile_token ? { turnstile_token } : {}) })),
  signup: (payload) => unwrap(client.post("/auth/signup", payload)),
  getMe: () => unwrap(client.get("/auth/me")),
  updateProfile: (payload) => unwrap(client.patch("/profile", payload)),

  // Phone verification (Twilio Verify) + SMS 2FA. Contract (mobile WEB_TEAM_SYNC_NOTES 2026-07-17):
  //   GET  /phone/status  → {phone, phone_verified, twofa_sms, sms_live, verified_at}
  //   POST /phone/start {phone}  → SMS (422 bad format · 409 number in use · 429 rate limit)
  //   POST /phone/verify {code}  → phone_verified (+13 score) (400 wrong · 410 expired · 429)
  //   POST /phone/2fa {enabled}  → {ok, twofa_sms} (400 "Verify your phone number first.")
  //   POST /auth/2fa/verify {ticket, code} → {token, user} (login challenge; 400 invalid · 410 expired)
  phoneStatus: () => unwrap(client.get("/phone/status")),
  phoneStart: (phone) => unwrap(client.post("/phone/start", { phone })),
  phoneVerify: (code) => unwrap(client.post("/phone/verify", { code })),
  phone2fa: (enabled) => unwrap(client.post("/phone/2fa", { enabled })),
  verify2fa: (ticket, code) => unwrap(client.post("/auth/2fa/verify", { ticket, code })),
  // Multi-device session management + silent refresh (Mobile parity 2026-06).
  listSessions: () => unwrap(client.get("/auth/sessions")),
  revokeSession: (sid) => unwrap(client.delete(`/auth/sessions/${sid}`)),
  revokeOtherSessions: () => unwrap(client.post("/auth/sessions/revoke-others")),
  refreshToken: () => unwrap(client.post("/auth/refresh")),

  // Beta feedback — in-app tester reports (POST /api/beta/feedback → admin beta feedback queue)
  submitBetaFeedback: (payload) => unwrap(client.post("/beta/feedback", payload)),
  // Reporter loop (§11.3): my reports, reply to the team, confirm a fix.
  myBetaFeedback: () => unwrap(client.get("/beta/feedback/mine")),
  replyBetaFeedback: (id, body) => unwrap(client.post(`/beta/feedback/${id}/reply`, { body })),
  confirmBetaFeedback: (id) => unwrap(client.post(`/beta/feedback/${id}/confirm`, {})),

  // People
  searchUsers: (q) => unwrap(client.get("/users/search", { params: { q } })),
  listContacts: () => unwrap(client.get("/contacts")),
  blockUser: (id) => unwrap(client.post(`/users/${id}/block`)),
  unblockUser: (id) => unwrap(client.delete(`/users/${id}/block`)),
  listBlocked: () => unwrap(client.get("/users/blocked")),

  // Connections
  sendConnectionRequest: (recipient_id, intro) =>
    unwrap(client.post("/connections/request", { recipient_id, intro })),
  connectionStatus: (userId) => unwrap(client.get(`/connections/status/${userId}`)),
  listRequests: () => unwrap(client.get("/connections/requests")),
  respondConnection: (connId, action) =>
    unwrap(client.post(`/connections/${connId}/respond`, { action })),

  // Invites
  myInvite: () => unwrap(client.get("/invites/mine")),
  // Referrals program — leaderboard (top connectors + my rank). Optional endpoint; hidden until backend ships it.
  referralLeaderboard: (period = "month") => unwrap(client.get(`/referrals/leaderboard?period=${period}`)),
  redeemInvite: (code) => unwrap(client.post("/invites/redeem", { code })),
  // Referral stats — newer endpoint (adds credits/uses); superset of /invites/mine.
  referralsMe: () => unwrap(client.get("/referrals/me")),

  // BLOQs
  listBloqs: () => unwrap(client.get("/circles")),
  createBloq: (payload) => unwrap(client.post("/circles", payload)),
  getBloq: (id) => unwrap(client.get(`/circles/${id}`)),
  joinBloq: (id) => unwrap(client.post(`/circles/${id}/join`)),
  leaveBloq: (id) => unwrap(client.post(`/circles/${id}/leave`)),
  deleteBloq: (id) => unwrap(client.delete(`/circles/${id}`)),
  pinBloqMessage: (bloqId, messageId) =>
    unwrap(client.post(`/circles/${bloqId}/pin`, { message_id: messageId })),
  unpinBloqMessage: (bloqId) => unwrap(client.delete(`/circles/${bloqId}/pin`)),
  // cc_23: BLOQ tab-pin toggle (list ordering) — distinct from the in-BLOQ message pin above
  pinBloqTop: (bloqId) => unwrap(client.post(`/circles/${bloqId}/pin-top`, {})),
  // Paid bloqs (owner controls + member join) — Stripe owned by backend
  setBloqMembership: (bloqId, payload) => unwrap(client.post(`/circles/${bloqId}/membership`, payload)),
  bloqMembershipCheckout: (bloqId, originUrl) =>
    unwrap(client.post(`/circles/${bloqId}/membership/checkout`, { origin_url: originUrl })),
  bloqAnalytics: (bloqId) => unwrap(client.get(`/circles/${bloqId}/analytics`)),

  // Chats
  listChats: () => unwrap(client.get("/chats")),
  badgeCount: () => unwrap(client.get("/badge-count")),
  openChat: (recipient_id) => unwrap(client.post("/chats", { recipient_id })),
  getMessages: (chatId) => unwrap(client.get(`/chats/${chatId}/messages`)),
  sendMessage: (chatId, text, imageB64, opts = {}) =>
    unwrap(client.post(`/chats/${chatId}/messages`, {
      text,
      ...(imageB64 ? { image_b64: imageB64 } : {}),
      ...(opts.viewOnce ? { view_once: true } : {}),
      ...(opts.replyTo ? { reply_to: opts.replyTo } : {}),
    })),
  sendEncryptedMessage: (chatId, e2ee, opts = {}) =>
    unwrap(client.post(`/chats/${chatId}/messages`, { e2ee, ...(opts.replyTo ? { reply_to: opts.replyTo } : {}) })),
  // cc_15 GIFs (Giphy proxy) — message carries gif{giphy_id,url,preview_url,width,height,title}, type "gif"
  gifsTrending: () => unwrap(client.get("/gifs/trending")),
  gifsSearch: (q) => unwrap(client.get("/gifs/search", { params: { q } })),
  sendGif: (chatId, gif, opts = {}) =>
    unwrap(client.post(`/chats/${chatId}/messages`, { text: "", gif, ...(opts.replyTo ? { reply_to: opts.replyTo } : {}) })),
  markRead: (chatId) => unwrap(client.post(`/chats/${chatId}/read`)),
  // Voice notes, view-once media, disappearing timer (Mobile parity 2026-06).
  sendVoice: (chatId, voiceB64, voiceDur, voiceWave) =>
    unwrap(client.post(`/chats/${chatId}/messages`, { voice_b64: voiceB64, voice_dur: voiceDur, voice_wave: voiceWave })),
  openViewOnce: (chatId, messageId) => unwrap(client.post(`/chats/${chatId}/messages/${messageId}/view-once`)),
  messageImageSrc: (chatId, messageId) => `/chats/${chatId}/messages/${messageId}/image`,
  messageVoiceSrc: (chatId, messageId) => `/chats/${chatId}/messages/${messageId}/voice`,
  setDisappearing: (chatId, seconds) => unwrap(client.patch(`/chats/${chatId}/disappearing`, { seconds })),
  reactMessage: (chatId, messageId, emoji) =>
    unwrap(client.post(`/chats/${chatId}/messages/${messageId}/react`, { emoji })),
  deleteMessage: (chatId, messageId) =>
    unwrap(client.delete(`/chats/${chatId}/messages/${messageId}`)),
  forwardMessage: (chatId, messageId, toChatId) =>
    unwrap(client.post(`/chats/${chatId}/messages/${messageId}/forward`, { to_chat_id: toChatId })),
  pinChat: (chatId) => unwrap(client.post(`/chats/${chatId}/pin`)),
  chatSummary: (chatId) => unwrap(client.post(`/chats/${chatId}/ai/summary`)),
  deleteChat: (chatId) => unwrap(client.delete(`/chats/${chatId}`)),
  // Interactive messages (Phase A parity) — polls, checklists, shared contacts, edit
  editMessage: (chatId, messageId, text) =>
    unwrap(client.patch(`/chats/${chatId}/messages/${messageId}`, { text })),
  createPoll: (chatId, payload) => unwrap(client.post(`/chats/${chatId}/polls`, payload)),
  votePoll: (chatId, messageId, option) =>
    unwrap(client.post(`/chats/${chatId}/messages/${messageId}/poll-vote`, { option })),
  createChecklist: (chatId, payload) => unwrap(client.post(`/chats/${chatId}/checklists`, payload)),
  toggleChecklist: (chatId, messageId, item) =>
    unwrap(client.post(`/chats/${chatId}/messages/${messageId}/checklist-toggle`, { item })),
  shareContact: (chatId, payload) => unwrap(client.post(`/chats/${chatId}/contacts`, payload)),

  // Circles = small group chats (≤20 members). Backend group-chat surface (POST/PATCH /chats/group*).
  // NOTE: distinct from BLOQs (large communities, /circles/* endpoints).
  createCircle: (name, memberIds) =>
    unwrap(client.post("/chats/group", { ...(name ? { name } : {}), member_ids: memberIds })),
  circleGroup: (chatId) => unwrap(client.get(`/chats/${chatId}/group`)),
  renameCircle: (chatId, payload) => unwrap(client.patch(`/chats/${chatId}/group`, payload)),
  addCircleMember: (chatId, userId) =>
    unwrap(client.post(`/chats/${chatId}/group/members`, { user_id: userId })),
  removeCircleMember: (chatId, memberId) =>
    unwrap(client.delete(`/chats/${chatId}/group/members/${memberId}`)),
  leaveCircle: (chatId) => unwrap(client.post(`/chats/${chatId}/group/leave`)),

  // Trust-score creation quotas for BLOQs + Circles. Backend-owned + authoritative; UI reads this
  // to show usage + soft-gate. Graceful: resolves null if the endpoint isn't live yet.
  myLimits: () => unwrap(client.get("/me/limits")).catch(() => null),

  // Trust / Notifications / Search (Phase 6 W3)
  scoreHistory: () => unwrap(client.get("/me/score-history")),
  notifications: () => unwrap(client.get("/notifications")),
  readNotifications: () => unwrap(client.post("/notifications/read")),
  search: (q) => unwrap(client.get("/search", { params: { q } })),

  // Channels
  listChannels: () => unwrap(client.get("/channels")),
  createChannel: (payload) => unwrap(client.post("/channels", payload)),
  updateChannel: (id, payload) => unwrap(client.patch(`/channels/${id}`, payload)),
  getChannel: (id) => unwrap(client.get(`/channels/${id}`)),
  getChannelPosts: (id) => unwrap(client.get(`/channels/${id}/posts`)),
  createChannelPost: (id, text) => unwrap(client.post(`/channels/${id}/posts`, { text })),
  subscribeChannel: (id) => unwrap(client.post(`/channels/${id}/subscribe`)),
  unsubscribeChannel: (id) => unwrap(client.post(`/channels/${id}/unsubscribe`)),

  // Shield
  shieldScan: (text) => unwrap(client.post("/shield/scan", { text })),
  shieldSummary: () => unwrap(client.get("/shield/summary")),
  // Shield Everywhere (cc_24): heuristic ruleset for on-device scanning of Cipher Lock text
  shieldRules: () => unwrap(client.get("/shield/rules")),

  // Reports
  createReport: (payload) => unwrap(client.post("/reports", payload)),

  // Subscriptions (Phase 6 W6) — Stripe owned by backend
  subscriptionStatus: () => unwrap(client.get("/subscriptions/status")),
  startTrial: () => unwrap(client.post("/subscriptions/trial/start")),
  subscriptionCheckout: (plan, origin_url, interval) =>
    unwrap(client.post("/subscriptions/checkout", { plan, origin_url, ...(interval ? { interval } : {}) })),
  subscriptionCheckoutStatus: (sessionId) =>
    unwrap(client.get(`/subscriptions/checkout/status/${sessionId}`)),
  // cc_21: auto-renewing subscriptions — cancel (access to period end) / resume / prorated switch / Stripe portal
  subscriptionCancel: () => unwrap(client.post("/subscriptions/cancel", {})),
  subscriptionResume: () => unwrap(client.post("/subscriptions/resume", {})),
  subscriptionSwitch: (plan, interval) => unwrap(client.post("/subscriptions/switch", { plan, ...(interval ? { interval } : {}) })),
  subscriptionPortal: (origin_url) => unwrap(client.post("/subscriptions/portal", { origin_url })),

  // Creator economy / monetization (P1) — Stripe + Stripe Connect owned by backend
  creatorStatus: () => unwrap(client.get("/creator/status")),
  creatorApply: (payload) => unwrap(client.post("/creator/apply", payload)),
  creatorConnectOnboard: (originUrl) =>
    unwrap(client.post("/creator/connect/onboard", { origin_url: originUrl })),
  creatorConnectStatus: () => unwrap(client.get("/creator/connect/status")),
  creatorEarnings: () => unwrap(client.get("/creator/earnings")),
  creatorMembershipCheckoutStatus: (sessionId) =>
    unwrap(client.get(`/creator/membership/checkout/${sessionId}`)),

  // Lane B — Creator fan tiers (monthly subscriptions). Stripe destination charges owned by backend.
  creatorTiers: (userId) => unwrap(client.get(`/creators/${userId}/tiers`)),
  createCreatorTier: (payload) => unwrap(client.post("/creators/me/tiers", payload)),
  updateCreatorTier: (tierId, payload) =>
    unwrap(client.patch(`/creators/me/tiers/${tierId}`, payload)),
  deleteCreatorTier: (tierId) => unwrap(client.delete(`/creators/me/tiers/${tierId}`)),
  creatorSubscribers: () => unwrap(client.get("/creators/me/subscribers")),

  // Lane B — fan side (subscribe + manage). Stripe checkout owned by backend; redirect to returned url.
  subscribeCreator: (userId, payload) => unwrap(client.post(`/creators/${userId}/subscribe`, payload)),
  creatorSubscribeStatus: (sessionId) => unwrap(client.get(`/creators/subscribe/checkout/${sessionId}`)),
  mySubscriptions: () => unwrap(client.get("/users/me/subscriptions")),

  // Lane B — ticketed bloq events. Paid tickets ride Stripe (backend owns fee math).
  bloqEvents: (bloqId, includePast = false) =>
    unwrap(client.get(`/circles/${bloqId}/events`, { params: { include_past: includePast } })),
  createBloqEvent: (bloqId, payload) => unwrap(client.post(`/circles/${bloqId}/events`, payload)),
  getEvent: (eventId) => unwrap(client.get(`/events/${eventId}`)),
  updateEvent: (eventId, payload) => unwrap(client.patch(`/events/${eventId}`, payload)),
  eventTicketCheckout: (eventId, payload) => unwrap(client.post(`/events/${eventId}/ticket/checkout`, payload)),
  eventTicketStatus: (eventId, sessionId) =>
    unwrap(client.get(`/events/${eventId}/ticket/checkout/${sessionId}`)),
  eventAttendees: (eventId) => unwrap(client.get(`/events/${eventId}/attendees`)),
  // Aggregated upcoming events across the user's bloqs (+ subscriptions_expiring_7d) — replaces per-bloq fan-out.
  upcomingEvents: () => unwrap(client.get("/me/upcoming-events")),
  // Public event share landing (crawler OG + human redirect + ?ref referral attribution). Backend-served.
  eventShareUrl: (eventId, ref) => `${API_BASE}/e/${eventId}${ref ? `?ref=${encodeURIComponent(ref)}` : ""}`,

  // Wallet proof (Phase 6 W2) — read/manage half (signing owned by mobile)
  walletMine: () => unwrap(client.get("/wallet/mine")),
  walletChains: () => unwrap(client.get("/wallet/chains")),
  walletTokenLookup: (payload) => unwrap(client.post("/wallet/token-lookup", payload)),

  // Wallet proof signing (Phase 6 §15.3) — EIP-191 (EVM) / Ed25519 (Solana)
  walletChallenge: (family, opts = {}) => unwrap(client.post("/wallet/challenge", { family, ...opts })),
  walletSubmit: (payload) => unwrap(client.post("/wallet/submit", payload)),
  walletChallengeStatus: (nonce) => unwrap(client.get(`/wallet/challenge/${nonce}`)),
  // Disconnect a verified wallet by family (evm | sol | btc) — DELETE /wallet/{family}.
  walletDelete: (family) => unwrap(client.delete(`/wallet/${family}`)),

  // Lane A ▸ A2 — set an owned NFT as a verified profile picture (manual entry or "pick from my NFTs" gallery).
  setNftAvatar: (payload) => unwrap(client.post("/users/me/nft-avatar", payload)), // {chain, contract, token_id}
  removeNftAvatar: () => unwrap(client.delete("/users/me/nft-avatar")),
  // Enumerate NFTs owned by the caller's verified wallet (Alchemy). 402 wallet_required if none verified.
  walletNfts: (chain, pageKey) => unwrap(client.get("/wallet/nfts", { params: { chain, ...(pageKey ? { page_key: pageKey } : {}) } })),
  // Lane A ▸ A3 — token-gated DMs. PUT sets a gate; DELETE reverts to contacts-only.
  setDmGate: (payload) => unwrap(client.put("/users/me/dm-gate", payload)), // {chain, kind, contract?, min_amount, symbol?}
  clearDmGate: () => unwrap(client.delete("/users/me/dm-gate")),

  // Circle Rooms + recordings (Phase 6 W4) — lifecycle only (no live audio transport yet)
  bloqRecordings: (bloqId) => unwrap(client.get(`/circles/${bloqId}/recordings`)),
  // cc_17 replay social proof: POST /recordings/{id}/listened once playback actually starts (idempotent).
  audioListened: (recId) => unwrap(client.post(`/recordings/${recId}/listened`, {})),
  recordingListens: (recId) => unwrap(client.get(`/recordings/${recId}/listens`)),
  recordingTranscript: (recordingId) => unwrap(client.get(`/recordings/${recordingId}/transcript`)),
  startRoom: (bloqId, title) =>
    unwrap(client.post(`/circles/${bloqId}/rooms`, title ? { title } : {})),
  getRoom: (roomId) => unwrap(client.get(`/rooms/${roomId}`)),
  joinRoom: (roomId) => unwrap(client.post(`/rooms/${roomId}/join`, {})),
  leaveRoom: (roomId) => unwrap(client.post(`/rooms/${roomId}/leave`, {})),
  raiseHand: (roomId) => unwrap(client.post(`/rooms/${roomId}/hand`, {})),
  promoteSpeaker: (roomId, userId) => unwrap(client.post(`/rooms/${roomId}/promote`, { user_id: userId })),
  demoteSpeaker: (roomId, userId) => unwrap(client.post(`/rooms/${roomId}/demote`, { user_id: userId })),
  endRoom: (roomId) => unwrap(client.post(`/rooms/${roomId}/end`, {})),

  // Circle admin (Phase 6 §15.4) — owner/admin controls
  updateBloq: (id, payload) => unwrap(client.patch(`/circles/${id}`, payload)),
  kickBloqMember: (id, memberId) => unwrap(client.delete(`/circles/${id}/members/${memberId}`)),
  setBloqMemberRole: (id, memberId, role) => unwrap(client.post(`/circles/${id}/members/${memberId}/role`, { role })),
  toggleBloqAutoTranslate: (id) => unwrap(client.post(`/circles/${id}/auto-translate`, {})),

  // Cipher Lock — read-only status (Phase 6 W5); establishing sessions is owned by mobile
  cipherlock: (chatId) => unwrap(client.get(`/chats/${chatId}/cipherlock`)),

  // Notification preferences (settings toggles)
  notificationPrefs: () => unwrap(client.get("/notifications/prefs")),
  updateNotificationPrefs: (payload) => unwrap(client.patch("/notifications/prefs", payload)),

  // What's new — latest published changelog entry for users (keyed to the app version)
  latestChangelog: (appVersion) =>
    unwrap(client.get("/changelog/latest", appVersion ? { params: { app_version: appVersion } } : undefined)),

  // Activity feed (v1.2.2) — replies to you + reactions on your messages.
  activity: () => unwrap(client.get("/activity")),
  readActivity: () => unwrap(client.post("/activity/read", {})),

  // BLOQ Topics (v1.2.2) — split a BLOQ into discussion areas; each has its own conversation_id.
  bloqTopics: (bloqId) => unwrap(client.get(`/circles/${bloqId}/topics`)),
  createBloqTopic: (bloqId, payload) => unwrap(client.post(`/circles/${bloqId}/topics`, payload)),
  updateBloqTopic: (bloqId, topicId, payload) => unwrap(client.patch(`/circles/${bloqId}/topics/${topicId}`, payload)),
  deleteBloqTopic: (bloqId, topicId) => unwrap(client.delete(`/circles/${bloqId}/topics/${topicId}`)),
  reorderBloqTopics: (bloqId, order) => unwrap(client.patch(`/circles/${bloqId}/topics/reorder`, { order })),

  // Account lifecycle — self-service deletion (password-confirmed)
  deleteAccount: (password) => unwrap(client.post("/auth/delete-account", { password })),

  // Change password (authenticated) — 200 {} on success; 401 "Current password is incorrect.";
  // 422 if new_password < 8 chars. Session stays valid (no token rotation).
  changePassword: (current_password, new_password) =>
    unwrap(client.post("/auth/change-password", { current_password, new_password })),

  // Password reset (Phase C) — external backend owns email delivery + code generation.
  // forgot-password always returns 200 (no user enumeration); reset-password validates the code.
  forgotPassword: (email, turnstile_token) =>
    unwrap(client.post("/auth/forgot-password", { email, ...(turnstile_token ? { turnstile_token } : {}) })),
  resetPassword: (email, code, new_password) =>
    unwrap(client.post("/auth/reset-password", { email, code, new_password })),

  // File attachments (Phase B) — chunked base64 upload. init → chunk* → complete.
  filesInit: (payload) => unwrap(client.post("/files/init", payload)),
  filesChunk: (payload) => unwrap(client.post("/files/chunk", payload)),
  filesComplete: (uploadId) => unwrap(client.post("/files/complete", { upload_id: uploadId })),
  // SEC-003: mint a short-lived (30-min) signed download URL — an opaque HMAC token scoped to
  // (file, user), NOT the session JWT. Preferred for all file/media downloads. → {url, token, expires_at}.
  mintDownloadUrl: (fileId) => unwrap(client.post(`/files/${fileId}/download-url`)),
  // DEPRECATED (session JWT in the query string) — kept only as a resilient fallback if minting fails.
  fileDownloadUrl: (fileId) =>
    `${API_BASE}/files/${fileId}/download?access_token=${encodeURIComponent(localStorage.getItem("bloq_token") || "")}`,

  // ── Web3 micro-transactions (flag-gated: REACT_APP_MICROTX) ──────────────────────────────
  // LIVE on the mobile-owned backend (2026-06). Final contracts in /app/HANDOFF_microtx_and_block.md.
  // Structured errors arrive as 4xx {"detail":{"error","message"}}.
  tipsConfig: () => unwrap(client.get("/tips/config")),
  tipCheckout: (payload) => unwrap(client.post("/tips/checkout", payload)),
  // Tipper-only; first "paid" poll settles idempotently + notifies the recipient.
  tipCheckoutStatus: (sessionId) => unwrap(client.post(`/tips/checkout/status/${sessionId}`, {})),
  tipsReceived: (cursor) => unwrap(client.get("/tips/received", { params: cursor ? { cursor } : {} })),
  tipsSent: (cursor) => unwrap(client.get("/tips/sent", { params: cursor ? { cursor } : {} })),
  // Token-/NFT-gated Circle join — reuses the wallet-proof flow (challenge → sign → verify).
  bloqGate: (bloqId) => unwrap(client.get(`/circles/${bloqId}/gate`)),
  // Owner-only: set/update/remove a Circle's token gate. type: "none"|"token"|"nft".
  setBloqGate: (bloqId, payload) => unwrap(client.put(`/circles/${bloqId}/gate`, payload)),
  verifyBloqGate: (bloqId, payload) => unwrap(client.post(`/circles/${bloqId}/gate/verify`, payload)),
  // Circle Boosts (Discord-style community boosting). No status endpoint — poll GET /boosts (lazy reconcile).
  bloqBoosts: (bloqId) => unwrap(client.get(`/circles/${bloqId}/boosts`)),
  bloqBoostCheckout: (bloqId, plan, originUrl) =>
    unwrap(client.post(`/circles/${bloqId}/boost/checkout`, { plan, origin_url: originUrl })),
  bloqBoostCancel: (bloqId) => unwrap(client.post(`/circles/${bloqId}/boost/cancel`, {})),
  // Sticker marketplace (creator rev-share).
  stickerPacks: (scope) => unwrap(client.get("/stickers/packs", { params: scope ? { scope } : {} })),
  stickerPack: (packId) => unwrap(client.get(`/stickers/packs/${packId}`)),
  stickerPackPurchaseCheckout: (packId, originUrl) =>
    unwrap(client.post(`/stickers/packs/${packId}/purchase/checkout`, { origin_url: originUrl })),
  stickerPurchaseStatus: (sessionId) => unwrap(client.post(`/stickers/purchase/status/${sessionId}`, {})),
  stickersOwned: () => unwrap(client.get("/stickers/owned")),
  // Creator studio (requires approved creator). Images are raw base64 (no data: prefix), ≤~300KB.
  createStickerPack: (payload) => unwrap(client.post("/creator/stickers/packs", payload)),
  addSticker: (packId, payload) => unwrap(client.post(`/creator/stickers/packs/${packId}/stickers`, payload)),
  publishStickerPack: (packId) => unwrap(client.post(`/creator/stickers/packs/${packId}/publish`, {})),
  // Sticker image is Bearer-auth'd (no token query param) — render via <AuthImage>, not a raw <img src>.
  stickerImageSrc: (stickerId) => `${API_BASE}/stickers/${stickerId}/image`,
  // Pack covers come back as Bearer-auth'd relative paths (/api/stickers/{id}/image); resolve to an
  // absolute backend URL so <AuthImage> fetches them through the authed client.
  stickerCoverSrc: (path) => (path ? `${API_BASE.replace(/\/api$/, "")}${path}` : null),
  sendSticker: (chatId, stickerId) => unwrap(client.post(`/chats/${chatId}/messages`, { sticker_id: stickerId })),
  // Best-effort share-funnel event ingest (mobile analytics endpoint — may 404 until shipped; errors swallowed).
  stickerShareEvent: (packId, type) => client.post(`/stickers/packs/${packId}/share-event`, { type }).then(() => true).catch(() => false),
  // Per-pack share analytics for the Creator Hub dashboard (mobile-owned; graceful when unavailable).
  creatorStickerAnalytics: () => unwrap(client.get("/creator/stickers/analytics")),
  // Public "top shared pack this week" for the Landing social-proof badge (mobile-owned; no auth; graceful 404).
  topSharedPack: () => unwrap(client.get("/stickers/top-shared", { params: { period: "week" } })),
  // Server-rendered OG share link (Handoff 3): crawlers get a rich per-pack card, humans redirect to the app.
  stickerShareUrl: (packId) => `${API_BASE}/s/${packId}`,
  // Weekly creator leaderboard by pack adds (mobile-owned; graceful when unavailable).
  creatorLeaderboard: () => unwrap(client.get("/creators/leaderboard", { params: { period: "week" } })),

  // Tip leaderboard ("Top tippers"). Mobile-owned; graceful shell until it ships.
  tipLeaderboard: (period = "week", scope = "global") =>
    unwrap(client.get("/tips/leaderboard", { params: { period, scope } })),

  // Lane A — in-chat crypto sends (LIVE on prod, Base Sepolia testnet).
  receivingWallet: (userId, chain) => unwrap(client.get(`/users/${userId}/receiving-wallet`, { params: chain ? { chain } : {} })),
  myReceivingWallet: (chain) => unwrap(client.get("/users/me/receiving-wallet", { params: chain ? { chain } : {} })),
  setReceivingWallet: (payload) => unwrap(client.put("/users/me/receiving-wallet", payload)),
  clearReceivingWallet: (chain) => unwrap(client.delete("/users/me/receiving-wallet", { params: chain ? { chain } : {} })),
  cryptoIntent: (payload) => unwrap(client.post("/payments/crypto/intent", payload)),
  cryptoConfirm: (payload) => unwrap(client.post("/payments/crypto/confirm", payload)),
  cryptoHistory: (cursor) => unwrap(client.get("/payments/crypto/history", { params: cursor ? { cursor } : {} })),

  // Lane D2 — Discovery directory (LIVE on prod after publish). Opaque cursor; echo next_cursor.
  discoverBloqs: (filter, cursor, q) => unwrap(client.get("/discover/circles", { params: { ...(filter ? { filter } : {}), ...(cursor ? { cursor } : {}), ...(q ? { q } : {}) } })),
  discoverChannels: (filter, cursor, q) => unwrap(client.get("/discover/channels", { params: { ...(filter ? { filter } : {}), ...(cursor ? { cursor } : {}), ...(q ? { q } : {}) } })),
  discoverPeople: (filter, cursor, q) => unwrap(client.get("/discover/people", { params: { ...(filter ? { filter } : {}), ...(cursor ? { cursor } : {}), ...(q ? { q } : {}) } })),

  // Lane D — public reputation (no auth required; 404 = private/unknown, indistinguishable).
  publicReputation: (handle) => unwrap(client.get(`/users/${handle}/reputation`)),

  // Lane C — AI (server-side model key). Shield explain is free; others share daily quotas.
  shieldExplain: (text) => unwrap(client.post("/shield/explain", { text })),
  smartReplies: (payload) => unwrap(client.post("/ai/smart-replies", payload)),
  translateMessage: (messageId, targetLang) => unwrap(client.post(`/messages/${messageId}/translate`, targetLang ? { target_lang: targetLang } : {})),
  // Babel (cc_24): free-text translation — `ephemeral:true` for Cipher Lock text (server never stores it)
  translateText: (text, ephemeral = false) => unwrap(client.post("/translate", { text, ephemeral })),
  translateBatch: (items, ephemeral = false) => unwrap(client.post("/translate/batch", { items, ephemeral })),
  toggleChatAutoTranslate: (chatId) => unwrap(client.post(`/chats/${chatId}/auto-translate`, {})),
  assistantSend: (message, opts = {}) => unwrap(client.post("/ai/assistant", { message, ...(opts.thread ? { thread: opts.thread } : {}) })),
  assistantHistory: (limit = 50) => unwrap(client.get("/ai/assistant/history", { params: { limit } })),
  assistantClear: () => unwrap(client.delete("/ai/assistant/history")),

  // ── Stories (v1.3, mobile parity) — ephemeral 24h stories ─────────────────────────────
  // Feed → {mine, items:[{user, count, latest_at, unseen, gated}]}. Per-user → {user, stories:[{...}]}.
  storiesFeed: () => unwrap(client.get("/stories/feed")),
  userStories: (ownerId) => unwrap(client.get(`/stories/user/${ownerId}`, { params: { media: "url" } })),
  // kind: text|image|video. text→{text,bg}; image→{image_b64,thumb_b64?}; video→{media_id (from chunked upload),thumb_b64?,duration_s}.
  // audience: contacts|circles|bloq. Returns the created story object.
  createStory: (payload) => unwrap(client.post("/stories", payload)),
  storyUploadInit: (payload) => unwrap(client.post("/stories/upload/init", payload)), // {name, mime, size}
  storyUploadChunk: (payload) => unwrap(client.post("/stories/upload/chunk", payload)), // {upload_id, index, data_b64}
  storyUploadComplete: (uploadId) => unwrap(client.post("/stories/upload/complete", { upload_id: uploadId })), // → {media_id}
  viewStory: (storyId) => unwrap(client.post(`/stories/${storyId}/view`, {})),
  storyViewers: (storyId) => unwrap(client.get(`/stories/${storyId}/viewers`)),
  replyStory: (storyId, text) => unwrap(client.post(`/stories/${storyId}/reply`, { text })),
  reactStory: (storyId, emoji) => unwrap(client.post(`/stories/${storyId}/react`, { emoji })), // cc_22: on-story reaction, no DM
  storyVideoUrl: (storyId) => unwrap(client.post(`/stories/${storyId}/video-url`, {})), // → signed url for video playback
  deleteStory: (storyId) => unwrap(client.delete(`/stories/${storyId}`)),

  // Legal docs (public JSON, cc_26): {title, effective, intro, sections[{heading, items[]}], contact_email}
  legal: (kind) => unwrap(client.get(`/legal/${kind}`)),

  // ── Voice / Video Calls (Stream Video — backend mints {stream:{api_key, token, call_type, call_id}}) ──
  // create → {call:{id,status:"ringing",video,caller,callee_id,...}}; token → {call, token, server_url, e2ee_wrap}.
  createCall: (chatId, opts = {}) =>
    unwrap(client.post("/calls", { chat_id: chatId, video: !!opts.video, e2ee: !!opts.e2ee, ...(opts.e2eeWrap ? { e2ee_wrap: opts.e2eeWrap } : {}) })),
  activeCall: () => unwrap(client.get("/calls/active")),
  callHistory: (limit = 30, before) => unwrap(client.get("/calls/history", { params: { limit, ...(before ? { before } : {}) } })),
  getCall: (callId) => unwrap(client.get(`/calls/${callId}`)),
  acceptCall: (callId) => unwrap(client.post(`/calls/${callId}/accept`, {})),
  declineCall: (callId) => unwrap(client.post(`/calls/${callId}/decline`, {})),
  ackCall: (callId) => unwrap(client.post(`/calls/${callId}/ack`, {})),
  endCall: (callId) => unwrap(client.post(`/calls/${callId}/end`, {})),
  hangupCall: (callId) => unwrap(client.post(`/calls/${callId}/hangup`, {})),
  callToken: (callId) => unwrap(client.post(`/calls/${callId}/token`, {})),
  // cc_9: web can't run frame crypto — downgrade E2EE symmetrically on join so the encrypted peer isn't black/silent.
  // Liveproof trust word (§2.10) — blob sealed client-side under the DM session key (server-blind)
  liveproofGet: (chatId) => unwrap(client.get(`/liveproof/${chatId}`)),
  liveproofSet: (chatId, blob) => unwrap(client.put(`/liveproof/${chatId}`, { blob })),
  liveproofClear: (chatId) => unwrap(client.delete(`/liveproof/${chatId}`)),
  liveproofWith: (peerId) => unwrap(client.get(`/liveproof/with/${peerId}`)),
  // cc_22: one call for the "My spaces" hub → {bloqs[], circles[], channels[], counts{owned,total}}
  mySpaces: () => unwrap(client.get("/me/spaces")),
  // Call history (§3.3c) — missed-call banner + call-back
  callHistory: () => unwrap(client.get("/calls/history")),
  clearCallHistory: () => unwrap(client.delete("/calls/history")),
  // Cipher Lock devices (§1.6): identity + every device fp; Forget pulls the device + its wraps.
  pqcIdentity: () => unwrap(client.get("/pqc/identity")),
  pqcForgetDevice: (fp) => unwrap(client.delete(`/pqc/devices/${fp}`)),
  // cc_12: server-driven Help Center FAQ + Getting Started checklist.
  helpArticles: () => unwrap(client.get("/help/articles")),
  helpChecklist: () => unwrap(client.get("/help/checklist")),

  // ── Live Rooms enhancements (mobile parity) ───────────────────────────────────────────
  roomTeaser: (roomId) => unwrap(client.get(`/rooms/${roomId}/teaser`)),
  roomReaction: (roomId, emoji) => unwrap(client.post(`/rooms/${roomId}/reactions`, { emoji })),
  roomVideo: (roomId, enable, keySid) => unwrap(client.post(`/rooms/${roomId}/video`, { enable, ...(keySid ? { key_sid: keySid } : {}) })),

  // ── Circle / Chat enhancements (mobile parity) ────────────────────────────────────────
  markBloqRead: (bloqId) => unwrap(client.post(`/circles/${bloqId}/read`, {})),
  muteBloqTopic: (bloqId, topicId, muted) => unwrap(client.post(`/circles/${bloqId}/topics/${topicId}/mute`, { muted })),
  setBloqNftIcon: (bloqId, payload) => unwrap(client.post(`/circles/${bloqId}/nft-icon`, payload)), // {chain, contract, token_id}
  promoteGroupAdmin: (chatId, memberId) => unwrap(client.post(`/chats/${chatId}/group/admins/${memberId}`, {})),

  // ── Group Cipher Lock code confirmation (server-persisted verify) ─────────────────────
  confirmGroupCipherCode: (bloqId, code) => unwrap(client.post(`/circles/${bloqId}/cipherlock/confirm`, { code })),
  unconfirmGroupCipherCode: (bloqId) => unwrap(client.delete(`/circles/${bloqId}/cipherlock/confirm`)),
};

export default api;
